import { useEffect, useRef, useState } from "react";
import { Play, RotateCcw, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

const SIZE = 20;      // grid cells
const CELL = 20;      // px per cell
const SPEED = 110;    // ms per tick

export default function SnakeGame({ onGameOver }) {
  const canvasRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  useEffect(() => {
    if (gameOver && onGameOver) onGameOver(score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameOver]);

  const state = useRef({
    snake: [{ x: 8, y: 10 }],
    dir: { x: 1, y: 0 },
    nextDir: { x: 1, y: 0 },
    food: { x: 14, y: 10 },
  });

  const reset = () => {
    state.current = {
      snake: [{ x: 8, y: 10 }],
      dir: { x: 1, y: 0 },
      nextDir: { x: 1, y: 0 },
      food: randFood([{ x: 8, y: 10 }]),
    };
    setScore(0);
    setGameOver(false);
    setRunning(true);
  };

  function randFood(snake) {
    let f;
    do { f = { x: Math.floor(Math.random() * SIZE), y: Math.floor(Math.random() * SIZE) }; }
    while (snake.some((s) => s.x === f.x && s.y === f.y));
    return f;
  }

  const changeDir = (nx, ny) => {
    const d = state.current.dir;
    if ((nx !== 0 && d.x === 0) || (ny !== 0 && d.y === 0)) {
      state.current.nextDir = { x: nx, y: ny };
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (["ArrowUp", "w"].includes(e.key)) changeDir(0, -1);
      else if (["ArrowDown", "s"].includes(e.key)) changeDir(0, 1);
      else if (["ArrowLeft", "a"].includes(e.key)) changeDir(-1, 0);
      else if (["ArrowRight", "d"].includes(e.key)) changeDir(1, 0);
      if (e.key.startsWith("Arrow")) e.preventDefault();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!running) return;
    const draw = () => {
      const ctx = canvasRef.current.getContext("2d");
      ctx.fillStyle = "#0A0A14";
      ctx.fillRect(0, 0, SIZE * CELL, SIZE * CELL);
      ctx.strokeStyle = "rgba(0,240,255,0.06)";
      for (let i = 0; i <= SIZE; i++) {
        ctx.beginPath(); ctx.moveTo(i * CELL, 0); ctx.lineTo(i * CELL, SIZE * CELL); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, i * CELL); ctx.lineTo(SIZE * CELL, i * CELL); ctx.stroke();
      }
      const { snake, food } = state.current;
      ctx.fillStyle = "#FF006E";
      ctx.fillRect(food.x * CELL + 2, food.y * CELL + 2, CELL - 4, CELL - 4);
      snake.forEach((s, i) => {
        ctx.fillStyle = i === 0 ? "#00F0FF" : "#0891b2";
        ctx.fillRect(s.x * CELL + 1, s.y * CELL + 1, CELL - 2, CELL - 2);
      });
    };

    const tick = () => {
      const st = state.current;
      st.dir = st.nextDir;
      const head = { x: st.snake[0].x + st.dir.x, y: st.snake[0].y + st.dir.y };
      if (head.x < 0 || head.y < 0 || head.x >= SIZE || head.y >= SIZE || st.snake.some((s) => s.x === head.x && s.y === head.y)) {
        setRunning(false);
        setGameOver(true);
        return;
      }
      st.snake.unshift(head);
      if (head.x === st.food.x && head.y === st.food.y) {
        setScore((s) => s + 10);
        st.food = randFood(st.snake);
      } else {
        st.snake.pop();
      }
      draw();
    };

    draw();
    const id = setInterval(tick, SPEED);
    return () => clearInterval(id);
  }, [running]);

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center justify-between w-full max-w-[400px] mb-4">
        <span className="font-arcade text-2xl text-[#F5C842]" data-testid="snake-score">SCORE {score}</span>
        <span className="font-arcade text-base text-slate-500">ARROWS / WASD</span>
      </div>
      <div className="relative scanlines border border-cyan-500/30">
        <canvas ref={canvasRef} width={SIZE * CELL} height={SIZE * CELL} className="block max-w-full" data-testid="snake-canvas" />
        {(!running) && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#0A0A14]/85 gap-4">
            {gameOver && <p className="font-arcade text-3xl text-[#FF006E]">GAME OVER</p>}
            <button onClick={reset} data-testid="snake-start-btn" className="font-arcade text-2xl px-8 py-3 bg-[#00F0FF] text-black border border-[#00F0FF] hover:shadow-[4px_4px_0px_#FF006E] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform duration-200 inline-flex items-center gap-2">
              {gameOver ? <><RotateCcw className="w-5 h-5" /> RETRY</> : <><Play className="w-5 h-5" /> START</>}
            </button>
          </div>
        )}
      </div>

      {/* Mobile D-pad */}
      <div className="mt-6 md:hidden select-none" data-testid="snake-dpad">
        <div className="flex justify-center">
          <DpadBtn testid="dpad-up" onPress={() => changeDir(0, -1)}><ChevronUp className="w-7 h-7" /></DpadBtn>
        </div>
        <div className="flex justify-center gap-3 my-3">
          <DpadBtn testid="dpad-left" onPress={() => changeDir(-1, 0)}><ChevronLeft className="w-7 h-7" /></DpadBtn>
          <DpadBtn testid="dpad-down" onPress={() => changeDir(0, 1)}><ChevronDown className="w-7 h-7" /></DpadBtn>
          <DpadBtn testid="dpad-right" onPress={() => changeDir(1, 0)}><ChevronRight className="w-7 h-7" /></DpadBtn>
        </div>
      </div>
    </div>
  );
}

function DpadBtn({ children, onPress, testid }) {
  const fire = (e) => { e.preventDefault(); onPress(); };
  return (
    <button
      data-testid={testid}
      onTouchStart={fire}
      onMouseDown={fire}
      className="w-16 h-16 flex items-center justify-center bg-[#11111A] border border-[#00F0FF]/50 text-[#00F0FF] active:bg-[#00F0FF] active:text-black transition-colors duration-100 touch-manipulation"
    >
      {children}
    </button>
  );
}
